/**
 * Paging of a report with the API's own `total`/`offset`/`hasMore` (batch 8 T14c). The page shown is the server's
 * offset, never a client guess. The range says the rows the page really returned (`offset + 1` to `offset + rows`), or
 * that the page has no rows; MUI's own clamped from/to is not used. The page step is the server's `limit` (next is
 * `offset + limit`, enabled by `hasMore`; the rows returned only shape the range text). A page past the end (the result shrank, or a
 * bookmarked offset) is said in words and offers the last page that exists. Every control is off while a request runs.
 */
import { Alert, Button, TablePagination } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { WirePagination } from '@/hooks/useInventoryReports';
import { PAGE_SIZES, displayedRange, isPastEnd, lastPageOffset, nextOffset, previousOffset } from './reportFilters';

export interface ReportPagerProps {
  pagination: WirePagination;
  /** Rows the page actually returned. */
  rows: number;
  onOffset: (offset: number) => void;
  onLimit?: (limit: number) => void;
  disabled?: boolean;
}

export function ReportPager({ pagination, rows, onOffset, onLimit, disabled = false }: ReportPagerProps) {
  const { t } = useTranslation('inventoryReports');
  const { total, limit, offset, hasMore } = pagination;
  const pastEnd = total > 0 && isPastEnd(pagination, rows);
  const lastOffset = lastPageOffset(total, limit);
  // MUI wants a page inside the result; the range text and the offsets below come from the server's offset, not from it.
  const page = Math.min(Math.floor(offset / limit), Math.floor(lastOffset / limit));
  const range = displayedRange(offset, rows);
  return (
    <>
      {pastEnd && (
        <Alert
          severity="info"
          sx={{ my: 1 }}
          action={
            <Button color="inherit" size="small" disabled={disabled} onClick={() => { onOffset(lastOffset); }}>
              {t('paging.lastPage')}
            </Button>
          }
        >
          {t('paging.pastEnd', { total })}
        </Alert>
      )}
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={limit}
        rowsPerPageOptions={onLimit === undefined ? [limit] : [...PAGE_SIZES]}
        onPageChange={(_event, next) => { onOffset(next > page ? nextOffset(pagination) : previousOffset(pagination, rows)); }}
        onRowsPerPageChange={(event) => { onLimit?.(Number(event.target.value)); }}
        labelRowsPerPage={t('paging.rowsPerPage')}
        labelDisplayedRows={() => (range === null ? t('paging.displayedNone', { count: total }) : t('paging.displayed', { ...range, count: total }))}
        slotProps={{
          select: { disabled },
          actions: {
            nextButton: { disabled: disabled || !hasMore, 'aria-label': t('paging.next') },
            previousButton: { disabled: disabled || offset === 0, 'aria-label': t('paging.previous') },
          },
        }}
      />
    </>
  );
}
